import { createTheme, ThemeProvider } from '@mui/material/styles';

import type {} from '@mui/x-data-grid-pro/themeAugmentation';

import type { FunctionComponent, PropsWithChildren } from 'react';

import './fonts/regola-font.css';

const theme = createTheme({
    components: {
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    '.MuiInput-root': {
                        padding: '10px',
                    },
                    '.MuiInput-root .MuiInput-input': {
                        padding: '0',
                    },
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#F1F1F0',
                    padding: '5px',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                outlined: {
                    ':active': {
                        backgroundColor: '#DADAD8',
                    },
                    ':hover': {
                        backgroundColor: '#DADAD8',
                        borderColor: '#DADAD8',
                    },
                    borderColor: '#DADAD8',
                    color: '#505052',
                },
                root: {
                    borderRadius: '0.5rem',
                    fontSize: '13px',
                    fontWeight: 600,
                },
            },
        },
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
        },
        MuiDataGrid: {
            styleOverrides: {
                cell: {
                    '&.MuiDataGrid-cell--withRenderer': {
                        border: 'none',
                    },
                    padding: '0',
                },
                columnHeader: {
                    '&:first-of-type': {
                        padding: '0 0 0 0.5rem',
                    },
                    '&:last-of-type': {
                        padding: '0 0.5rem 0 0',
                    },
                    borderBottom: 'none',
                    padding: '0',
                    paddingRight: 'none',
                    textTransform: 'uppercase',
                },
                columnHeaders: {
                    backgroundColor: '#F1F1F0',
                    border: 'none',
                    borderRadius: '0.5rem',
                    boxShadow:
                        '0.8px 1px 1px 0px rgba(251, 251, 255, 0.70) inset, -1px -0.8px 1px 0px rgba(251, 251, 255, 0.50) inset, 0px 3px 1px 0px rgba(80, 80, 82, 0.01), 0px 2px 1px 0px rgba(80, 80, 82, 0.05), 0px 1px 1px 0px rgba(80, 80, 82, 0.09)',
                    height: '2.25rem',
                },
                main: {
                    gap: '1rem',
                },
                root: {
                    '& .MuiDataGrid-virtualScroller': {
                        scrollbarWidth: 'none',
                    },
                    '& .MuiDataGrid-withBorderColor': {
                        border: 'none',
                    },
                    border: 'none',
                    color: '#9A9A98',
                },
                row: {
                    '&:last-of-type': {
                        borderBottom: 'none',
                    },
                    ':hover': {
                        backgroundColor: 'transparent',
                    },
                    borderBottom: '1px solid #E7E7E7',
                    height: '3.75rem',
                },
                withBorderColor: {
                    '&.MuiDataGrid-cell--withRenderer': {
                        border: 'none',
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#FBFBFF',
                    borderRadius: '0.75rem',
                    boxShadow: '0px 2px 8px 0px rgba(154, 154, 152, 0.08), 0px 0px 24px 0px rgba(110, 110, 108, 0.10)',
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    borderTop: '1px solid #F1F1F0',
                    padding: '1rem',
                },
            },
        },
        MuiDialogContentText: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: '1rem',
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    '&.MuiDivider-root': {
                        borderColor: '#F1F1F0',
                        margin: '0',
                    },
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#FBFBFF',
                    boxShadow:
                        '-16px 0px 6px 0px rgba(80, 80, 82, 0.01), -9px 0px 5px 0px rgba(80, 80, 82, 0.03), -4px 0px 4px 0px rgba(80, 80, 82, 0.04), -1px 0px 2px 0px rgba(80, 80, 82, 0.05)',
                    color: '#040F16',
                    padding: '2rem',
                    width: '550px',
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    color: '#505052',
                    fontSize: '13px',
                    fontWeight: 600,
                    lineHeight: '1rem',
                },
            },
        },
        MuiInput: {
            styleOverrides: {
                root: {
                    fontSize: '14px',
                    fontWeight: 400,
                    letterSpacing: '-0.14px',
                    lineHeight: '1.25rem',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    padding: 0,
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    padding: '0',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': {
                        '&:focus': {
                            background: 'transparent',
                        },
                        ':hover': {
                            backgroundColor: '#F1F1F0',
                        },
                        backgroundColor: 'transparent',
                        color: '#F55321',
                    },
                    ':active': {
                        backgroundColor: '#E7E7E7',
                    },
                    ':hover': {
                        backgroundColor: '#F1F1F0',
                    },
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '1.25rem',
                    overflow: 'hidden',
                    padding: '0.625rem 0.75rem',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: '#F6F6F7',
                    borderRadius: '0.75rem',
                    boxShadow:
                        '0.8px 1px 1px 0px rgba(251, 251, 255, 0.70) inset, -1px -0.8px 1px 0px rgba(251, 251, 255, 0.50) inset, 0px 17px 7px 0px rgba(80, 80, 82, 0.01), 0px 9px 6px 0px rgba(80, 80, 82, 0.05), 0px 4px 4px 0px rgba(80, 80, 82, 0.09), 0px 1px 2px 0px rgba(80, 80, 82, 0.10)',
                    padding: '0.25rem',
                    scrollbarWidth: 'none',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    '&.Mui-focused': {
                        backgroundColor: '#F6F6F7',
                    },
                    borderRadius: '0.5rem',
                    color: '#6E6E6C',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    padding: '0.375rem 0.5rem',
                },
                select: {
                    '&:focus': {
                        backgroundColor: 'transparent',
                    },
                },
            },
        },
    },
    shadows: [
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
        'none',
    ],
    typography: {
        button: {
            textTransform: 'none',
        },
        fontFamily: 'FT Regola Neue',
        fontSize: 14,
    },
});

export const LayoutThemeProvider: FunctionComponent<PropsWithChildren> = (props) => {
    return <ThemeProvider theme={theme}>{props.children}</ThemeProvider>;
};
